const STORAGE_KEY = 'portal_recent_login_identifier';
const TTL_MS = 30 * 60 * 1000;

/**
 * Identificador (CPF, CNPJ ou e-mail) que a pessoa acabou de digitar no `/login` do portal, guardado
 * só para preencher o campo de novo se ela voltar ao login antes de entrar — por exemplo, quando o
 * link de acesso falha e ela pede outro.
 *
 * Contrato (LGPD — é dado pessoal em claro, sem opt-in, por isso o escopo é o menor possível):
 * - `sessionStorage`: vale só para a aba onde foi digitado. Link aberto do WhatsApp ou do e-mail
 *   cai em outra aba ou aparelho, onde nada é preenchido — limitação aceita, não bug.
 * - Expira em 30 minutos.
 * - Some quando a sessão do portal abre (layout autenticado), seja qual for o caminho de entrada,
 *   e no "Esquecer" do `/login`. Assim, quem clica em "Sair" não deixa o documento preenchido para
 *   a próxima pessoa na mesma aba.
 *
 * Não confundir com o "lembrar de mim" opt-in, que fica no servidor e só devolve um hint mascarado.
 */
export function saveRecentPortalLoginIdentifier(identifier: string): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ identifier, savedAt: Date.now() }));
  } catch {
    return;
  }
}

/** Identificador salvo por {@link saveRecentPortalLoginIdentifier}, ou `null` se ausente ou vencido. */
export function loadRecentPortalLoginIdentifier(): string | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) {
      clearRecentPortalLoginIdentifier();
      return null;
    }

    const record = parsed as Record<string, unknown>;
    if (
      typeof record.identifier !== 'string' ||
      typeof record.savedAt !== 'number' ||
      Date.now() - record.savedAt > TTL_MS ||
      Date.now() < record.savedAt
    ) {
      clearRecentPortalLoginIdentifier();
      return null;
    }

    return record.identifier;
  } catch {
    return null;
  }
}

/** Apaga o identificador salvo por {@link saveRecentPortalLoginIdentifier}. */
export function clearRecentPortalLoginIdentifier(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    return;
  }
}
